import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Languages, Play, Square, Loader2, CheckCircle, AlertCircle, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';
import { toast } from 'sonner';

const LANG_LABELS = {
  en: 'English', de: 'Deutsch', fr: 'Francais', es: 'Espanol',
  ar: 'العربية', 'zh-CN': '中文', it: 'Italiano', ru: 'Русский', pt: 'Portugues', fa: 'فارسی'
};

const LANG_FLAGS = {
  en: '🇬🇧', de: '🇩🇪', fr: '🇫🇷', es: '🇪🇸',
  ar: '🇸🇦', 'zh-CN': '🇨🇳', it: '🇮🇹', ru: '🇷🇺', pt: '🇵🇹', fa: '🇮🇷'
};

export default function TranslationCard() {
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const pollRef = useRef(null);
  const prevTranslatedRef = useRef(null);
  const authToastShownRef = useRef(false);
  const [pollAuthError, setPollAuthError] = useState(false);

  const locale = (i18n.language || 'en').split('-')[0];

  const fetchStatus = useCallback(async () => {
    try {
      const res = await api.get('/seller/products/translate/status');
      const d = res.data;
      setPollAuthError(false);
      authToastShownRef.current = false;
      const prev = prevTranslatedRef.current;
      const cur = d?.translated_count;
      prevTranslatedRef.current = cur;
      setStatus(d);
    } catch (err) {
      const st = err.response?.status;
      if (st === 401 || st === 403) {
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
        setPollAuthError(true);
        if (!authToastShownRef.current) {
          authToastShownRef.current = true;
          toast.error(t('sellerTranslation.pollAuthError'), { duration: 8000 });
        }
      }
    }
  }, [t]);

  useEffect(() => {
    fetchStatus();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchStatus]);

  useEffect(() => {
    if (status?.status === 'running') {
      if (!pollRef.current) {
        pollRef.current = setInterval(fetchStatus, 3000);
      }
    } else {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }
  }, [status?.status, fetchStatus]);

  const startTranslation = async () => {
    try {
      setLoading(true);
      const res = await api.post('/seller/products/translate/start');
      toast.success(res.data.message);
      await fetchStatus();
    } catch (err) {
      const msg = err.response?.data?.detail || t('sellerTranslation.startFailed');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const startMissingTranslation = async (languages = ['fa']) => {
    try {
      setLoading(true);
      const res = await api.post('/seller/products/translate/start-missing', { languages });
      if ((res.data?.total_to_translate || 0) > 0) {
        toast.success(res.data.message);
      } else {
        toast.warning(res.data?.message || t('sellerTranslation.fillMissingFailed'));
      }
      await fetchStatus();
    } catch (err) {
      const msg = err.response?.data?.detail || t('sellerTranslation.fillMissingFailed');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const stopTranslation = async () => {
    try {
      setLoading(true);
      const res = await api.post('/seller/products/translate/stop');
      toast.info(res.data.message);
      await fetchStatus();
    } catch {
      toast.error(t('sellerTranslation.stopFailed'));
    } finally {
      setLoading(false);
    }
  };

  const resetTranslations = async () => {
    try {
      setResetting(true);
      const res = await api.post('/seller/products/translate/reset');
      toast.success(res.data.message);
      setShowResetConfirm(false);
      await fetchStatus();
    } catch (err) {
      const msg = err.response?.data?.detail || t('sellerTranslation.resetFailed');
      toast.error(msg);
    } finally {
      setResetting(false);
    }
  };

  if (!status) return null;

  const { total_products, translated_count, pending_count, percentage, translated_languages, missing_languages = [], language_stats, status: runStatus, error } = status;
  const isRunning = runStatus === 'running';
  const isPaused = runStatus === 'paused';
  const isCompleted = runStatus === 'completed';
  const hasMissingLanguages = missing_languages.length > 0;
  const allDone = total_products > 0 && !hasMissingLanguages;
  const hasTranslations = translated_count > 0 || (translated_languages?.length > 0);

  const dateLocale = locale === 'tr' ? 'tr-TR' : locale === 'de' ? 'de-DE' : 'en-US';

  return (
    <Card className="border-l-4 border-l-violet-500" data-testid="translation-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Languages className="h-5 w-5 text-violet-600" />
            <CardTitle className="text-base">{t('sellerTranslation.title')}</CardTitle>
          </div>
          <div className="flex items-center gap-2">
            {hasTranslations && !isRunning && (
              showResetConfirm ? (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-red-600">{t('sellerTranslation.confirmReset')}</span>
                  <Button size="sm" variant="destructive" onClick={resetTranslations}
                    disabled={resetting} data-testid="confirm-reset-btn" className="h-7 text-xs">
                    {resetting ? <Loader2 className="h-3 w-3 animate-spin" /> : t('sellerTranslation.yes')}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowResetConfirm(false)}
                    className="h-7 text-xs" data-testid="cancel-reset-btn">
                    {t('sellerTranslation.no')}
                  </Button>
                </div>
              ) : (
                <Button size="sm" variant="outline" onClick={() => setShowResetConfirm(true)}
                  className="text-red-600 border-red-200 hover:bg-red-50 h-7 text-xs"
                  data-testid="reset-translation-btn">
                  <Trash2 className="h-3 w-3 mr-1" />
                  {t('sellerTranslation.reset')}
                </Button>
              )
            )}
            {isRunning ? (
              <Button size="sm" variant="destructive" onClick={stopTranslation} disabled={loading} data-testid="stop-translation-btn">
                <Square className="h-3.5 w-3.5 mr-1.5" />
                {t('sellerTranslation.stop')}
              </Button>
            ) : hasMissingLanguages ? (
              <Button size="sm" onClick={() => startMissingTranslation(missing_languages)} disabled={loading}
                className="bg-amber-600 hover:bg-amber-700" data-testid="fill-missing-translation-btn">
                {loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Play className="h-3.5 w-3.5 mr-1.5" />}
                {t('sellerTranslation.fillMissing')}
              </Button>
            ) : (
              <Button size="sm" onClick={startTranslation} disabled={loading || (allDone && isCompleted)}
                className="bg-violet-600 hover:bg-violet-700" data-testid="start-translation-btn">
                {loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> :
                  isPaused ? <Play className="h-3.5 w-3.5 mr-1.5" /> :
                  allDone ? <CheckCircle className="h-3.5 w-3.5 mr-1.5" /> :
                  <Play className="h-3.5 w-3.5 mr-1.5" />}
                {isPaused ? t('sellerTranslation.resume') : allDone ? t('sellerTranslation.completed') : t('sellerTranslation.translateAll')}
              </Button>
            )}
          </div>
        </div>
        {total_products > 0 && (
          <CardDescription className="text-xs mt-1">
            {allDone
              ? t('sellerTranslation.descAllDone')
              : hasMissingLanguages
                ? t('sellerTranslation.descMissingLangs', { langs: missing_languages.join(', ') })
                : t('sellerTranslation.descPending', { count: total_products })}
          </CardDescription>
        )}
      </CardHeader>

      <CardContent className="space-y-4">
        {total_products > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground flex items-center gap-1.5">
                {isRunning && <Loader2 className="h-3.5 w-3.5 animate-spin text-violet-500" />}
                {isRunning ? t('sellerTranslation.statusTranslating') :
                  isPaused ? t('sellerTranslation.statusPaused') :
                  isCompleted ? t('sellerTranslation.statusCompleted') : t('sellerTranslation.statusReady')}
              </span>
              <span className="font-mono font-medium text-sm" data-testid="translation-progress-text">
                {translated_count} / {total_products}
              </span>
            </div>
            <Progress value={percentage} className="h-3" data-testid="translation-progress-bar" />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{t('sellerTranslation.translated')} <strong className="text-green-600">{translated_count}</strong></span>
              <span>{t('sellerTranslation.remaining')} <strong className="text-amber-600">{pending_count}</strong></span>
              <span className="font-semibold text-violet-600">%{percentage}</span>
            </div>
            {status.updated_at && (
              <p className="text-[11px] text-muted-foreground font-mono">
                {t('sellerTranslation.serverUpdate')}{' '}
                {(() => {
                  try {
                    return new Date(status.updated_at).toLocaleString(dateLocale);
                  } catch {
                    return status.updated_at;
                  }
                })()}
              </p>
            )}
            {status.heartbeat_lang && isRunning && (
              <p className="text-[11px] text-violet-800 bg-violet-50 border border-violet-100 rounded px-2 py-1 font-mono">
                {t('sellerTranslation.processingLang')}{' '}
                <strong>
                  {status.heartbeat_lang}
                  {typeof status.heartbeat_lang_index === 'number' &&
                    typeof status.heartbeat_lang_total === 'number' &&
                    ` (${status.heartbeat_lang_index}/${status.heartbeat_lang_total})`}
                </strong>
              </p>
            )}
            {isRunning && (
              <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded px-2 py-1">
                {t('sellerTranslation.runningHint')}
              </p>
            )}
          </div>
        )}

        {pollAuthError && (
          <div className="flex items-center gap-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 px-3 py-2 rounded-md" data-testid="translation-auth-error">
            <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
            {t('sellerTranslation.authError')}
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 px-3 py-2 rounded-md" data-testid="translation-error">
            <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
            {error}
          </div>
        )}

        {(translated_languages.length > 0 || language_stats) && (
          <div className="space-y-1.5">
            <p className="text-xs text-muted-foreground font-medium">{t('sellerTranslation.translatedLanguages')}</p>
            <div className="flex flex-wrap gap-1.5" data-testid="translated-languages">
              {Object.entries(LANG_LABELS).map(([code, label]) => {
                const stat = language_stats?.[code];
                const done = stat ? stat.percentage >= 100 : translated_languages.includes(code);
                const pct = stat?.percentage;
                return (
                  <Badge key={code} variant={done ? 'default' : 'outline'}
                    className={done ? 'bg-green-100 text-green-800 border-green-300 text-[11px]' : 'text-gray-400 text-[11px]'}>
                    <span className="mr-1">{LANG_FLAGS[code]}</span>
                    {label}
                    {typeof pct === 'number' && !done && <span className="ml-1 opacity-70">%{pct}</span>}
                    {done && <CheckCircle className="h-3 w-3 ml-1" />}
                  </Badge>
                );
              })}
            </div>
          </div>
        )}

        {total_products === 0 && (
          <p className="text-sm text-muted-foreground text-center py-2">
            {t('sellerTranslation.noProducts')}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
